import React, { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useMusic } from '../../context/MusicContext';
import { useMode } from '../../context/ModeContext';
import { Rewind, Play, Pause, FastForward, X } from 'lucide-react';

import { 
  PlayerWrapper, PlayerContainer, PillContent, AlbumArtContainer, AlbumArtImage, 
  TrackInfo, Title, Artist, ProgressCircle,
  SoundWaveContainer, SoundWaveBar, BottomControls, ControlButton,
  ExpandedContent, ExpandedHeader, LargeAlbumArt, HeaderText, PlaylistList, PlaylistItem, 
  ItemIndex, ListItemArt, ItemInfo, ItemDuration, LinearProgressContainer, LinearProgressBar, CloseButton
} from './MusicPlayer.styles';

/* --- SMOOTH TUNED AUDIO WAVE COMPONENT --- */
const AudioWave = ({ isPlaying }) => {
  const { analyserRef } = useMusic();
  const barsRef = useRef([]);
  
  // 1. Store the current visual scale of each bar to track its position across frames
  const currentScalesRef = useRef(new Array(12).fill(0.2));

  useEffect(() => {
    let animationFrame;
    const dataArray = new Uint8Array(32);
    const numBars = 12;

    const renderFrame = () => {
      if (analyserRef.current && isPlaying) {
        analyserRef.current.getByteFrequencyData(dataArray);
        
        barsRef.current.forEach((bar, index) => {
          if (bar) {
            const edgeTaperMultiplier = Math.sin(Math.PI * (index / (numBars - 1)));
            const freqBin = index + 2; 
            const rawValue = dataArray[freqBin] || 0;
            const normalized = rawValue / 255;
            
            let dynamicScale = Math.pow(normalized, 2) * 1.7; 
            dynamicScale = dynamicScale * edgeTaperMultiplier;
            
            // This is the TARGET scale the audio data wants the bar to snap to
            const targetScale = Math.min(1.0, 0.2 + dynamicScale);
            
            // 2. THE MAGIC: LERP (Linear Interpolation)
            // Move only 25% of the distance toward the target scale per frame.
            // This absorbs the micro-jitters while keeping the macro-beat pulse.
            currentScalesRef.current[index] += (targetScale - currentScalesRef.current[index]) * 0.40;
            
            bar.style.transform = `scaleY(${currentScalesRef.current[index]})`;
          }
        });
      } else if (!isPlaying) {
        // Reset smoothly when paused
        barsRef.current.forEach((bar, index) => {
          if (bar) {
            currentScalesRef.current[index] = 0.2; 
            bar.style.transform = 'scaleY(0.2)';
          }
        });
      }
      
      if (isPlaying) {
        animationFrame = requestAnimationFrame(renderFrame);
      }
    };

    renderFrame();
    return () => cancelAnimationFrame(animationFrame);
  }, [isPlaying, analyserRef]);

  return (
    <SoundWaveContainer>
      {[...Array(12)].map((_, i) => (
        <SoundWaveBar key={i} ref={el => barsRef.current[i] = el} $isPlaying={isPlaying} />
      ))}
    </SoundWaveContainer>
  );
};

/* --- MAIN MUSIC PLAYER --- */
const MusicPlayer = () => {
  const { isTerminalExpanded } = useMode();
  const { 
    isVisible, isPlaying, progress, currentTrack, playlistData,
    togglePlay, nextTrack, prevTrack, selectTrack, handleTimeUpdate, setIsPlaying, audioRef 
  } = useMusic();

  const [isExpanded, setIsExpanded] = useState(false);
  const hoverTimeoutRef = useRef(null);

  const radius = 8;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => setIsExpanded(true), 300);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setIsExpanded(false);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);


  if (!currentTrack) return null;

  return (
    <>
      <audio 
        ref={audioRef}
        crossOrigin="anonymous" 
        onTimeUpdate={handleTimeUpdate}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={nextTrack}
        src={currentTrack.src}
      />

      <AnimatePresence>
        {isVisible && (
          <PlayerWrapper>
            <PlayerContainer
              layout
              $isPlaying={isPlaying}
              $isExpanded={isExpanded}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              initial={{ y: 50, opacity: 0, scale: 0.9 }}
              // 3. UPDATE ANIMATE PROP: Push down to y: 150 when terminal expands
              animate={{ 
                y: isTerminalExpanded ? 150 : 0, 
                opacity: isTerminalExpanded ? 0 : 1, 
                scale: isTerminalExpanded ? 0.9 : 1 
              }}
              exit={{ y: 50, opacity: 0, scale: 0.9 }}
              transition={{ layout: { type: 'spring', stiffness: 300, damping: 30 } }}
            >
              {/* Add the close button right here at the top level of the container */}
                {isExpanded && (
                  <CloseButton 
                    as={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2, delay: 0.1 }} 
                    $isPlaying={isPlaying} 
                    onClick={(e) => {
                      e.stopPropagation(); // Prevents the click from accidentally triggering any underlying elements
                      setIsExpanded(false); // Manually minimizes the player
                    }}
                  >
                    <X size={16} strokeWidth={2.5} />
                  </CloseButton>
                )}
              
              {!isExpanded ? (
                /* --- INACTIVE STATE (PILL) --- */
                <PillContent layout>
                  <AlbumArtContainer layout>
                    <AlbumArtImage src={currentTrack.cover} alt="album cover" />
                  </AlbumArtContainer>
                  
                  <TrackInfo layout>
                    {/* layout="position" prevents the text from stretching and distorting */}
                    <Title as={motion.span} layout="position">
                      {currentTrack.title}
                    </Title>
                    <Artist as={motion.span} layout="position" $isPlaying={isPlaying}>
                      {currentTrack.artist}
                    </Artist>
                  </TrackInfo>

                  <AudioWave isPlaying={isPlaying} />

                  <motion.div layout style={{ display: 'flex', alignItems: 'center' }}>
                    <ProgressCircle $isPlaying={isPlaying}>
                      <circle cx="10" cy="10" r={radius} strokeWidth="2.5" fill="none" />
                      <circle cx="10" cy="10" r={radius} strokeWidth="2.5" fill="none" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" />
                    </ProgressCircle>
                  </motion.div>
                </PillContent>
              ) : (
                /* --- ACTIVE STATE (EXPANDED LIST) --- */
                <ExpandedContent layout>
                  <ExpandedHeader>
                    <LargeAlbumArt src={currentTrack.cover} alt="album cover" />
                    <HeaderText>
                      <Title as={motion.span} layout="position" style={{ fontSize: '1.2rem' }}>
                        {currentTrack.title}
                      </Title>
                      <Artist as={motion.span} layout="position" $isPlaying={isPlaying}>
                        {currentTrack.artist}
                      </Artist>
                    </HeaderText>
                  </ExpandedHeader>

                  <PlaylistList>
                    {playlistData.map((track, index) => {
                      const isActive = index === currentTrack.id - 1; 
                      return (
                        <PlaylistItem 
                          key={track.id} 
                          $active={isActive} 
                          $isPlaying={isPlaying}
                          onClick={() => {
                            if (isActive) togglePlay();
                            else selectTrack(index);
                          }}
                        >
                          <ItemIndex $isPlaying={isPlaying}>
                            {isActive && isPlaying ? (
                              <Pause fill="currentColor" stroke="none" size={14} />
                            ) : isActive && !isPlaying ? (
                              <Play fill="currentColor" stroke="none" size={14} />
                            ) : (
                              track.id
                            )}
                          </ItemIndex>
                            <ListItemArt src={track.cover} alt={`${track.title} cover`} />
                          <ItemInfo>
                            <Title style={{ fontSize: '0.9rem', color: isActive ? (isPlaying ? '#1db954' : 'inherit') : 'inherit' }}>
                              {track.title}
                            </Title>
                            <Artist $isPlaying={isPlaying}>{track.artist}</Artist>
                          </ItemInfo>
                          <ItemDuration $isPlaying={isPlaying}>{track.duration}</ItemDuration>
                        </PlaylistItem>
                      );
                    })}
                  </PlaylistList>

                  <BottomControls>
                    <ControlButton $isPlaying={isPlaying} onClick={prevTrack}>
                      <Rewind fill="currentColor" stroke="none" />
                    </ControlButton>
                    
                    <ControlButton $primary $isPlaying={isPlaying} onClick={togglePlay}>
                      {isPlaying ? (
                        <Pause fill="currentColor" stroke="none" />
                      ) : (
                        <Play fill="currentColor" stroke="none" style={{ marginLeft: '2px' }} />
                      )}
                    </ControlButton>
                    
                    <ControlButton $isPlaying={isPlaying} onClick={nextTrack}>
                      <FastForward fill="currentColor" stroke="none" />
                    </ControlButton>
                  </BottomControls>

                  <LinearProgressContainer $isPlaying={isPlaying}>
                    <LinearProgressBar $isPlaying={isPlaying} progress={progress} />
                  </LinearProgressContainer>
                </ExpandedContent>
              )}
            </PlayerContainer>
          </PlayerWrapper>
        )}
      </AnimatePresence>
    </>
  );
};

export default MusicPlayer;